import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { msg } from '@/plugins/message'
import { TOKEN_KEY } from '@/api/auth-storage'
import { apiConfig } from './config'
import { ApiError, getApiMessage, isApiResponse, isBusinessSuccessCode } from './types'
import type { RequestConfig, UploadConfig, DownloadConfig } from './types'
import { secureLocalStorage } from '@/utils/secureLocalStorage'

type RequestInterceptor = (ctx: { url: string; config: AxiosRequestConfig }) => void
type ResponseInterceptor = <T>(ctx: { data: T; response: AxiosResponse }) => T | Promise<T>

/** 与 axios 请求一并传递，供错误拦截器判断是否弹出全局提示 */
type AxiosConfigWithFlags = InternalAxiosRequestConfig & { skipErrorMessage?: boolean }

/** 请求拦截器队列（在 axios 实例上注册） */
const requestInterceptorFns: RequestInterceptor[] = []

/** 响应拦截器队列（在请求完成后手动执行） */
const responseInterceptors: ResponseInterceptor[] = []

/** 添加请求拦截器 */
export function addRequestInterceptor(fn: RequestInterceptor) {
  requestInterceptorFns.push(fn)
}

/** 添加响应拦截器 */
export function addResponseInterceptor(fn: ResponseInterceptor) {
  responseInterceptors.push(fn)
}

/** 创建 axios 实例 */
const axiosInstance = axios.create({
  baseURL: apiConfig.baseURL || undefined,
  timeout: apiConfig.timeout,
  headers: apiConfig.headers,
})

const SESSION_EXPIRED_HINT = '当前登录已失效，请重新登录'
const NETWORK_ERROR_HINT = '网络异常，请稍后重试'
const ERROR_TOAST_DEDUP_MS = 2500

/** 鉴权失效踢下线：同步占位，并发 401 共用同一 Promise，避免叠 toast */
let sessionKickoff: Promise<void> | null = null
/** 触发踢下线时的 token，用于区分「尚未清掉的旧 token」与「重新登录的新 token」 */
let kickedTokenValue = ''

const recentErrorToasts = new Map<string, number>()

function readStoredToken(): string {
  try {
    return String(secureLocalStorage.getItem(TOKEN_KEY) ?? '').trim()
  } catch {
    return ''
  }
}

/** 重新登录写入新 token 后，允许后续业务请求 */
function clearSessionKickoffIfLoggedIn(): void {
  const token = readStoredToken()
  if (sessionKickoff && token && token !== kickedTokenValue) {
    sessionKickoff = null
    kickedTokenValue = ''
  }
}

/**
 * 登录页匿名接口：会话失效拦截期间仍须放行。
 * 否则踢下线后验证码 / 登录因子 / 发短信 / 登录本身在拦截器里被直接 reject，Network 里看不到请求。
 */
const ANONYMOUS_AUTH_PATHS = [
  '/auth/b/getPicCaptcha',
  '/auth/b/getPhoneValidCode',
  '/auth/b/loginFactors',
  '/auth/b/sendBoundPhoneValidCode',
  '/auth/b/doLogin',
  '/auth/b/doLoginByPhone',
  '/auth/b/jitLoginRandom',
  '/auth/b/doLoginByJitCert',
  '/auth/b/getLoginConfig',
  '/auth/b/getQrLoginCode',
  '/auth/b/getQrLoginStatus',
  '/auth/c/getPicCaptcha',
  '/auth/c/getPhoneValidCode',
  '/auth/c/doLogin',
  '/auth/c/doLoginByPhone',
  '/biz/config/systemPanel/public',
  '/sys/userCenter/getPicCaptcha',
  '/sys/userCenter/findPasswordGetPhoneValidCode',
  '/sys/userCenter/findPasswordGetEmailValidCode',
  '/sys/userCenter/findPasswordByPhone',
  '/sys/userCenter/findPasswordByEmail',
] as const

function resolveRequestPath(url: string): string {
  const raw = url.trim()
  if (!raw) return ''
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname
    }
  } catch {
    /* 非法绝对地址时退回原串截 query */
  }
  const q = raw.indexOf('?')
  return q >= 0 ? raw.slice(0, q) : raw
}

function isAnonymousAuthRequest(url: string): boolean {
  const path = resolveRequestPath(url)
  if (!path) return false
  return ANONYMOUS_AUTH_PATHS.some((p) => path === p || path.endsWith(p))
}

function shouldAbortOutgoingRequest(url: string): boolean {
  if (!sessionKickoff) return false
  if (isAnonymousAuthRequest(url)) return false
  const token = readStoredToken()
  return !token || token === kickedTokenValue
}

/**
 * Sa-Token NotLoginException（业务码/HTTP 401）：无效、过期、冻结、踢下线、顶下线等。
 * 不含 403 无权限/无角色、不含账号封禁。
 */
function isGenericHttpStatusMessage(message: string): boolean {
  return /^request failed with status code \d+$/i.test(message.trim())
}

/** 客户端超时、网关超时，或后端返回的超时文案 */
function isTimeoutFailure(
  message: string,
  httpStatus: number | undefined,
  axiosCode?: string,
): boolean {
  if (httpStatus === 408 || httpStatus === 504) return true
  if (axiosCode === 'ECONNABORTED' || axiosCode === 'ETIMEDOUT') return true
  return /timeout of \d+ms exceeded|timeout exceeded|\bETIMEDOUT\b|gateway timeout|^请求超时$|^接口超时$|^连接超时$/i.test(
    message.trim(),
  )
}

function resolveTimeoutMessage(
  message: string,
  httpStatus: number | undefined,
  axiosCode?: string,
): string {
  return isTimeoutFailure(message, httpStatus, axiosCode) ? NETWORK_ERROR_HINT : message
}

function isSessionExpiredAuthFailure(
  code: number | undefined,
  httpStatus: number | undefined,
  message: string,
): boolean {
  const unauthorized = code === 401 || httpStatus === 401
  if (!unauthorized) return false
  const m = message.trim()
  if (!m || isGenericHttpStatusMessage(m)) return true
  if (/封禁|无权限|无角色|forbidden/i.test(m) && !/冻结|踢下线|顶下线|未登录|过期|失效/.test(m)) {
    return false
  }
  if (/未登录|登录已过期|登录已失效|请重新登录|未能读取到有效|账号已冻结|已冻结/.test(m)) {
    return true
  }
  const mentionsToken = /token/i.test(m) || /令牌/.test(m)
  if (!mentionsToken) return true
  return /(无效|过期|冻结|踢下线|顶下线|已失效|expired|invalid|kicked|replaced|frozen|not[\s-]?login)/i.test(
    m,
  )
}

function toastErrorOnce(message: string): void {
  const key = message.trim()
  if (!key) return
  if (sessionKickoff) return
  const now = Date.now()
  const last = recentErrorToasts.get(key) ?? 0
  if (now - last < ERROR_TOAST_DEDUP_MS) return
  recentErrorToasts.set(key, now)
  if (recentErrorToasts.size > 40) {
    for (const [k, t] of recentErrorToasts) {
      if (now - t > ERROR_TOAST_DEDUP_MS) recentErrorToasts.delete(k)
    }
  }
  msg.error(key)
}

/** 后端将 null 序列化为字符串 "null" 时解包 */
function normalizePayloadData<T>(data: unknown): T {
  if (data === 'null') return null as T
  return data as T
}

function createApiError(
  errMsg: string,
  code: number | undefined,
  status: number | undefined,
  data: unknown,
): ApiError {
  return new ApiError(errMsg, code, status, normalizePayloadData(data))
}

function rejectSilentSessionExpired(
  code: number,
  status: number | undefined,
  data: unknown,
): Promise<never> {
  return Promise.reject(createApiError(SESSION_EXPIRED_HINT, code, status, data))
}

/** 清空登录态、提示并跳转登录页（并发只执行一次，提示不含 token 原文） */
function handleSessionExpired(): Promise<void> {
  if (sessionKickoff) return sessionKickoff
  kickedTokenValue = readStoredToken()
  sessionKickoff = (async () => {
    try {
      msg.closeAll()
      const { useAuthStore } = await import('@/stores/auth')
      const router = (await import('@/router')).default
      useAuthStore().clearSessionLocal()
      msg.warning(SESSION_EXPIRED_HINT)
      const route = router.currentRoute.value
      if (route.name !== 'login') {
        await router.replace({ name: 'login', query: { redirect: route.fullPath } })
      }
    } catch {
      /* 跳转失败仍保持 kickoff，避免再次刷屏 */
    }
  })()
  return sessionKickoff
}

function rejectAfterSessionExpired(
  code: number,
  status: number | undefined,
  data: unknown,
): Promise<never> {
  return handleSessionExpired().then(() => rejectSilentSessionExpired(code, status, data))
}

async function throwIfSessionExpired(
  code: number,
  errMsg: string,
  status: number,
  data: unknown,
): Promise<void> {
  if (!isSessionExpiredAuthFailure(code, status, errMsg)) return
  await handleSessionExpired()
  throw createApiError(SESSION_EXPIRED_HINT, code, status, data)
}

function throwBusinessApiError(
  errMsg: string,
  code: number,
  status: number,
  data: unknown,
  skipErrorMessage: boolean,
): never {
  const display = resolveTimeoutMessage(errMsg, status)
  if (!skipErrorMessage) toastErrorOnce(display)
  throw createApiError(display, code, status, data)
}

/** 注册请求拦截器（请求时依次执行队列中的 fn） */
axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  clearSessionKickoffIfLoggedIn()
  if (shouldAbortOutgoingRequest(config.url ?? '')) {
    return Promise.reject(createApiError(SESSION_EXPIRED_HINT, 401, 401, undefined)) as never
  }
  for (const fn of requestInterceptorFns) {
    fn({ url: config.url ?? '', config })
  }
  return config
})

/** axios 非 2xx 或网络错误：若 body 为标准 ApiResponse，则统一提示并抛出 ApiError */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (error instanceof ApiError && error.message === SESSION_EXPIRED_HINT) {
      return Promise.reject(error)
    }
    const ax = error as {
      code?: string
      config?: AxiosConfigWithFlags
      response?: AxiosResponse<unknown>
      message?: string
    }
    const silent = ax.config?.skipErrorMessage === true
    const res = ax.response
    const body = res?.data
    const axiosMessage = String(ax.message ?? '')
    if (body !== undefined && isApiResponse(body)) {
      const rawMsg = getApiMessage(body)
      if (isSessionExpiredAuthFailure(body.code, res?.status, rawMsg)) {
        return rejectAfterSessionExpired(body.code, res?.status, body.data)
      }
      const errMsg = resolveTimeoutMessage(rawMsg, res?.status, ax.code)
      if (!silent) toastErrorOnce(errMsg)
      return Promise.reject(createApiError(errMsg, body.code, res?.status, body.data))
    }
    if (isSessionExpiredAuthFailure(undefined, res?.status, axiosMessage)) {
      return rejectAfterSessionExpired(401, res?.status, undefined)
    }
    if (isTimeoutFailure(axiosMessage, res?.status, ax.code)) {
      if (!silent) toastErrorOnce(NETWORK_ERROR_HINT)
      return Promise.reject(createApiError(NETWORK_ERROR_HINT, undefined, res?.status, body))
    }
    const fallback = axiosMessage || NETWORK_ERROR_HINT
    if (!silent) toastErrorOnce(fallback)
    return Promise.reject(ax)
  },
)

/** 执行响应拦截器 */
async function runResponseInterceptors<T>(data: T, response: AxiosResponse<T>): Promise<T> {
  let result = data
  for (const fn of responseInterceptors) {
    result = await fn({ data: result, response })
  }
  return result
}

/**
 * 统一请求方法（基于 axios）
 */
export async function request<T>(url: string, config: RequestConfig = {}): Promise<T> {
  const {
    skipInterceptor = false,
    skipErrorMessage = false,
    body,
    ...rest
  } = config as RequestConfig & {
    body?: unknown
  }

  const axiosConfig = {
    ...rest,
    url,
    method: (rest.method as AxiosRequestConfig['method']) ?? 'GET',
    data: body !== undefined ? body : rest.data,
    timeout: rest.timeout ?? apiConfig.timeout,
    skipErrorMessage,
  } as AxiosConfigWithFlags
  // FormData 上传时不带 Content-Type，由浏览器自动设置 multipart/form-data; boundary=...
  if (body instanceof FormData) {
    const raw = (
      typeof axiosConfig.headers === 'object' &&
      axiosConfig.headers &&
      !Array.isArray(axiosConfig.headers)
        ? axiosConfig.headers
        : {}
    ) as Record<string, unknown>
    axiosConfig.headers = { ...raw, 'Content-Type': false } as AxiosRequestConfig['headers']
  }

  const response = await axiosInstance.request<unknown>(axiosConfig)
  const data = response.data

  if (response.status < 200 || response.status >= 300) {
    const rawMessage = isApiResponse(data)
      ? getApiMessage(data)
      : response.statusText || `HTTP ${response.status}`
    const code = isApiResponse(data) ? data.code : undefined
    const payload = isApiResponse(data) ? (data as { data: unknown }).data : data
    await throwIfSessionExpired(code ?? response.status, rawMessage, response.status, payload)
    const message = resolveTimeoutMessage(rawMessage, response.status)
    if (!skipErrorMessage) {
      toastErrorOnce(message)
    }
    throw createApiError(message, code, response.status, payload)
  }

  if (skipInterceptor) {
    return data as T
  }

  if (isApiResponse<unknown>(data)) {
    if (!isBusinessSuccessCode(data.code)) {
      const errMsg = getApiMessage(data)
      await throwIfSessionExpired(
        data.code,
        errMsg,
        response.status,
        (data as { data: unknown }).data,
      )
      throwBusinessApiError(
        errMsg,
        data.code,
        response.status,
        (data as { data: unknown }).data,
        skipErrorMessage,
      )
    }
    return runResponseInterceptors(
      normalizePayloadData<T>((data as { data: T }).data),
      response as AxiosResponse<T>,
    )
  }

  return runResponseInterceptors(data as T, response as AxiosResponse<T>)
}

/** 从 Content-Disposition 头解析文件名 */
function getFilenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined
  const match = header.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  if (match?.[1]) return decodeURIComponent(match[1].trim())
  const simple = header.match(/filename=["']?([^"';]+)["']?/i)
  return simple?.[1]?.trim()
}

/**
 * 文件上传（FormData，multipart/form-data）
 * 不设置 Content-Type，由 axios 自动添加 boundary
 */
export async function upload<T = unknown>(
  url: string,
  formData: FormData,
  config: UploadConfig = {},
): Promise<T> {
  return request<T>(url, {
    ...config,
    method: 'POST',
    body: formData,
  } as RequestConfig)
}

/**
 * 获取文件流（Blob），用于下载或前端处理
 */
export async function getBlob(url: string, config: DownloadConfig = {}): Promise<Blob> {
  const { responseType = 'blob', timeout, ...rest } = config
  const response = await axiosInstance.request<Blob>({
    ...rest,
    url,
    method: 'GET',
    responseType,
    timeout: timeout ?? apiConfig.timeout,
  })
  if (response.status < 200 || response.status >= 300) {
    throw new ApiError(
      response.statusText || `HTTP ${response.status}`,
      undefined,
      response.status,
      response.data,
    )
  }
  return response.data
}

/**
 * 触发浏览器下载文件
 * @param url 下载地址
 * @param filename 保存的文件名（可选，不传则从 Content-Disposition 解析）
 */
export async function download(
  url: string,
  filename?: string,
  config: DownloadConfig = {},
): Promise<void> {
  const { timeout, responseType, ...rest } = config
  const response = await axiosInstance.request<Blob>({
    ...rest,
    url,
    method: 'GET',
    responseType: responseType ?? 'blob',
    timeout: timeout ?? apiConfig.timeout,
  })
  if (response.status < 200 || response.status >= 300) {
    throw new ApiError(
      response.statusText || `HTTP ${response.status}`,
      undefined,
      response.status,
      response.data,
    )
  }
  const blob = response.data
  const errMsg = await readBlobApiErrorMessage(blob)
  if (errMsg) {
    toastErrorOnce(errMsg)
    throw createApiError(errMsg, undefined, response.status, blob)
  }
  const name =
    filename ??
    getFilenameFromContentDisposition(response.headers['content-disposition'] ?? null) ??
    'download'
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = name
  link.click()
  URL.revokeObjectURL(link.href)
}

/** blob 下载时若后端仍返回业务 JSON（HTTP 200），避免把错误正文存成 PDF */
async function readBlobApiErrorMessage(blob: Blob): Promise<string | null> {
  if (!(blob instanceof Blob) || blob.size === 0 || blob.size > 4096) {
    return null
  }
  try {
    const text = await blob.slice(0, 4096).text()
    const trimmed = text.trim()
    if (!trimmed.startsWith('{')) {
      return null
    }
    const parsed = JSON.parse(trimmed) as { code?: number; msg?: string; message?: string }
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    if (typeof parsed.code === 'number' && isBusinessSuccessCode(parsed.code)) {
      return null
    }
    if (parsed.msg == null && parsed.message == null && parsed.code == null) {
      return null
    }
    if (typeof parsed.msg === 'string' && parsed.msg.trim()) return parsed.msg.trim()
    if (typeof parsed.message === 'string' && parsed.message.trim()) return parsed.message.trim()
    const fromApi = getApiMessage(parsed)
    return fromApi && fromApi !== '请求失败' ? fromApi : '下载失败'
  } catch {
    return null
  }
}

/** HTTP 方法封装 */
export const http = {
  get: <T>(url: string, config?: Omit<RequestConfig, 'body' | 'data'>) =>
    request<T>(url, { ...config, method: 'GET' } as RequestConfig),

  post: <T>(url: string, body?: unknown, config?: Omit<RequestConfig, 'body' | 'data'>) =>
    request<T>(url, { ...config, method: 'POST', body } as RequestConfig),

  put: <T>(url: string, body?: unknown, config?: Omit<RequestConfig, 'body' | 'data'>) =>
    request<T>(url, { ...config, method: 'POST', body } as RequestConfig),

  patch: <T>(url: string, body?: unknown, config?: Omit<RequestConfig, 'body' | 'data'>) =>
    request<T>(url, { ...config, method: 'PATCH', body } as RequestConfig),

  delete: <T>(url: string, config?: Omit<RequestConfig, 'body' | 'data'>) =>
    request<T>(url, { ...config, method: 'POST' } as RequestConfig),

  /** 文件上传（FormData） */
  upload: <T = unknown>(url: string, formData: FormData, config?: UploadConfig) =>
    upload<T>(url, formData, config ?? {}),

  /** 获取文件 Blob（不触发下载） */
  getBlob: (url: string, config?: DownloadConfig) => getBlob(url, config),

  /** 触发浏览器下载文件 */
  download: (url: string, filename?: string, config?: DownloadConfig) =>
    download(url, filename, config),
}
